// Range/threshold segment pills — mutually-exclusive numeric choices, count-anchored, single-select. Each
// segment carries the whole condition it applies; the panel commits its operator + value on pick. The
// active segment carries the primary accent (border + fill), matching EnumValuePills — no check mark.

import { cn } from '@/_shared/lib/utils';

import { useRovingListFocus } from './hooks/useRovingListFocus';
import { InsightSummary } from './InsightSummary';
import { isTotalEstimated } from './lib/coverage-text';
import { formatCount } from './lib/format';
import {
  PILL_BASE,
  PILL_COUNT,
  PILL_COUNT_SELECTED,
  PILL_COUNT_UNSELECTED,
  PILL_SELECTED,
  PILL_UNSELECTED,
} from './lib/pill-styles';
import type { RowSegment } from './types';

export function SegmentPills({
  segments,
  total,
  totalAccuracy,
  activeIndex,
  onPick,
  ariaLabel,
  estimated = false,
}: {
  segments: RowSegment[];
  total?: number;
  /**
   * The catalog's confidence in `total`. Distinct from {@link estimated}, which marks the band COUNTS as
   * derived: this marks the DENOMINATOR as a catalog estimate. Both controls publish the same row count,
   * so both must disclose it the same way — that parity is why the row builder emits one qualifier.
   */
  totalAccuracy?: string;
  activeIndex: number; // index of the currently-matching segment, or -1
  onPick: (segment: RowSegment) => void;
  ariaLabel: string;
  /** Band counts are derived from quantiles, never measured — surfaced verbatim from the row. */
  estimated?: boolean;
}) {
  const { refs, roving, setFocusIndex, onKeyDown } = useRovingListFocus(segments.length, activeIndex);

  const active = activeIndex >= 0 ? segments[activeIndex] : undefined;

  return (
    <div className="flex flex-col gap-2">
      {/* `active?.count` is passed through UNCOALESCED: `?? 0` here would turn "we don't know" into
          a confident "0 satisfy this condition". */}
      <InsightSummary
        matchCount={active?.count}
        total={total}
        totalEstimated={isTotalEstimated(total, totalAccuracy)}
        unset={!active}
        unsetLabel="None selected"
        estimated={estimated}
      />
      <div role="listbox" aria-label={ariaLabel} className="flex flex-wrap gap-1.5 p-0.5">
        {segments.map((seg, i) => {
          const isSelected = i === activeIndex;
          return (
            <button
              key={seg.label}
              ref={(el) => {
                refs.current[i] = el;
              }}
              type="button"
              role="option"
              aria-selected={isSelected}
              tabIndex={i === roving ? 0 : -1}
              onFocus={() => setFocusIndex(i)}
              onKeyDown={onKeyDown}
              onClick={() => onPick(seg)}
              className={cn(PILL_BASE, isSelected ? PILL_SELECTED : PILL_UNSELECTED)}
            >
              <span>{seg.label}</span>
              {seg.count !== undefined && (
                <span className={cn(PILL_COUNT, isSelected ? PILL_COUNT_SELECTED : PILL_COUNT_UNSELECTED)}>
                  {formatCount(seg.count)}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
